import type { ReactNode } from "react";
import { useMemo } from "react";
import type useAgentChat from "../../hooks/agent/useAgentChat";
import { Box } from "../../ui/box";
import { Text } from "../../ui/text";
import { VStack } from "../../ui/vstack";
import { AGENT_ACTIONS } from "./actions";
import type { AgentIllustrations } from "./AnimationSlot";
import { useAnimationMoment } from "./AnimationSlot";
import type { AgentFormContextValue } from "./answers/form/AgentFormContext";
import { AgentFormProvider } from "./answers/form/AgentFormContext";
import ConceptSetActions from "./answers/form/ConceptSetActions";
import FormMessage from "./answers/form/FormMessage";
import {
  deriveFormFlow,
  isWaitingForScript,
  stageCaption,
} from "./answers/form/formFlow";
import LiveConcepts from "./answers/form/LiveConcepts";
import StageCaption from "./answers/form/StageCaption";
import Composer from "./Composer";
import ErrorBubble from "./ErrorBubble";
import MessageList from "./MessageList";
import StreamingBubble from "./StreamingBubble";
import useAgentScroll, { useForceScrollOn } from "./useAgentScroll";

type Props = {
  /**
   * The chat state, from `useAgentChat(sessionId)`.
   *
   * Passed in rather than fetched here, because the hook owns the websocket
   * subscription and calling it twice would open two of them — and a screen that wants
   * anything alongside the chat (a live preview of what the agent is building, say)
   * needs the same state this component is rendering.
   */
  chat: ReturnType<typeof useAgentChat>;
  placeholder?: string;
  /** The glass illustrations `AnimationSlot`'s moments are built around. See its
   * own docstring for why this lives on the consumer rather than in the kit. */
  illustrations?: AgentIllustrations;
  /** Moment 4 — "Draft creado". See `AnimationSlot`'s docstring. */
  finalMoment?: ReactNode | null;
  /**
   * Shown in place of the composer on an old, read-only chat — the consumer knows
   * where a new one is started, this kit does not.
   */
  readOnlyFooter?: ReactNode;
};

const StalledNotice = () => (
  <Box className="mb-3 self-start rounded-2xl border border-warning-300 bg-warning-50 px-4 py-3">
    <Text className="text-sm text-warning-700">
      Esto está tardando más de lo normal. Puedes volver a escribir.
    </Text>
  </Box>
);

/**
 * A complete chat with one agent — and it knows nothing about any particular agent.
 *
 * A live session draws the cards the backend posts; a retired one (an old
 * interview-style chat) is a read-only transcript with no composer.
 */
const AgentChat = ({
  chat,
  placeholder,
  illustrations,
  finalMoment,
  readOnlyFooter,
}: Props) => {
  const {
    messages,
    store,
    isStreaming,
    isThinking,
    tools,
    error,
    stalled,
    locked,
    sending,
    loading,
    hasOlder,
    loadOlder,
    send,
    dismissError,
    formMode,
    readOnly,
    formLive,
  } = chat;

  const scroll = useAgentScroll();

  // Where the form-first flow is, read off the persisted thread. Cheap (one pass over
  // the messages) and only meaningful in a FORM session, where the cards live.
  const flow = useMemo(() => deriveFormFlow(messages), [messages]);
  // The form still waiting for an answer is drawn last, under whatever is streaming:
  // the Logistics card is posted the moment a concept is chosen, before the script it
  // follows is written, and the thread order would put the script under its questions.
  const pendingForm = useMemo(
    () =>
      [...messages]
        .reverse()
        .find(
          (message) =>
            !!message.form &&
            !message.formAnswer &&
            !flow.answered[message.form.formId],
        ),
    [messages, flow],
  );
  const transcript = useMemo(
    () =>
      pendingForm
        ? messages.filter((message) => message !== pendingForm)
        : messages,
    [messages, pendingForm],
  );
  const caption = formMode
    ? stageCaption({ flow, live: formLive, jobRunning: locked })
    : null;

  const formContext = useMemo<AgentFormContextValue>(
    () => ({
      sessionId: chat.sessionId,
      flow,
      live: formLive,
      can: chat.can,
      busy: locked,
      waitingForScript: isWaitingForScript(flow, formLive.scriptReady, locked),
      submitForm: chat.submitForm,
      requestFormOptions: chat.requestFormOptions,
      selectConcept: chat.selectConcept,
      regenerateConcepts: chat.regenerateConcepts,
    }),
    [
      chat.sessionId,
      flow,
      formLive,
      locked,
      chat.can,
      chat.submitForm,
      chat.requestFormOptions,
      chat.selectConcept,
      chat.regenerateConcepts,
    ],
  );

  // The single source of truth for "is there a moment card on screen right now" —
  // shared with the fallback below so the thinking bubble only ever appears when
  // this slot is genuinely empty, never as a second, redundant "something is
  // happening" cue stacked under a moment that already says so.
  const { node: moment, key: momentKey } = useAnimationMoment({
    tools,
    store,
    isStreaming,
    startedAt: chat.turn.startedAt,
    illustrations,
    finalMoment,
  });

  // A failed turn FORCES the view down to its error, pinned or not.
  //
  // Everything else in this chat scrolls only if the user is already at the bottom —
  // correct, because yanking the viewport away from someone re-reading scrollback is
  // rude. An error is the exception: a turn that failed silently, below the fold, is
  // indistinguishable from one that is still thinking.
  useForceScrollOn(scroll, error);

  // A moment card APPEARING is the same "the chat just moved and the person needs to
  // see it" situation — it fires on every transition into a new card (no card -> a
  // card, or one card -> a different one), not just once per turn. `momentKey` only
  // changes on exactly that transition (see `useAnimationMoment`'s docstring), so this
  // does not re-fire for the stage text rotating or a thumbnail arriving inside the
  // same card, and it does nothing when a card disappears (`momentKey` going back to
  // null) — there is nothing new to scroll to for that.
  useForceScrollOn(scroll, momentKey);

  // Everything transient, in the order it happens: what the agent is doing, what it
  // is saying, and how it went wrong. Each piece is retired by its own rule — the
  // streaming bubble when the persisted answer lands, the error only by the user.
  //
  // `moment` owns its own visibility: normally gated to the turn actually being
  // live (see `isStreaming` below), while moment 4 is not "in progress" news at
  // all, so it is not gated here.
  //
  // `thinkingFallback` is the chat's "never look stuck" guarantee: whenever the
  // turn is live and there is no moment card to narrate it — no matter why, a tool
  // this build does not recognise, a gap between one moment ending and the next
  // starting, anything — the "Pensando…" bubble fills that slot instead.
  const overlay = (
    <>
      {moment}
      {stalled ? <StalledNotice /> : null}
      {/* Form-first: the concepts as they stream in, the "Generar otros" under a
          finished set, and the stage line (also a live region). They sit before the
          streaming bubble so the script, when it streams, reads as the last thing. */}
      {formMode ? (
        <>
          <LiveConcepts />
          <ConceptSetActions />
          <StageCaption caption={caption} />
        </>
      ) : null}
      {isStreaming ? (
        <StreamingBubble
          store={store}
          thinking={isThinking}
          startedAt={chat.turn.startedAt}
          // The stage line already says what is happening; a "Pensando…" bubble under
          // it would say the same thing twice.
          thinkingFallback={!moment && !caption}
        />
      ) : null}
      {pendingForm ? (
        <FormMessage key={pendingForm.id} message={pendingForm} />
      ) : null}
      {error ? <ErrorBubble message={error} onDismiss={dismissError} /> : null}
    </>
  );

  return (
    <AgentFormProvider value={formContext}>
      <VStack className="flex-1">
        <MessageList
          messages={transcript}
          overlay={overlay}
          hasOlder={hasOlder}
          loadingOlder={loading}
          onLoadOlder={loadOlder}
          scroll={scroll}
        />
        {readOnly ? (
          readOnlyFooter
        ) : (
          <Composer
            onSend={send}
            disabled={locked || sending || !chat.can(AGENT_ACTIONS.sendMessage)}
            placeholder={placeholder}
          />
        )}
      </VStack>
    </AgentFormProvider>
  );
};

export default AgentChat;
